import { useTranslation } from '@/composables/useTranslation'
import { StoredEditingSession } from '@/models/Import'
import Message from '@/models/Message'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { browserStorage } from '@/utils/browserStorage'
import { Debouncer } from '@/utils/Debouncer'
import { getProductInputEntries } from '@/utils/importUtils'
import { objectsAreEqual } from '@/utils/tools'
import { defineStore } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { LocationQuery, LocationQueryValue, useRoute } from 'vue-router'
import { useProductsBulkStore } from './productsBulkStore'
import { usePropertyStore } from './propertyStore'

export const BlankQueryKey = '_blankSession'
export const BlankQuery = { [BlankQueryKey]: 'Yes' } as const
export const UsedAsBaseKey = '_usedAsBase' as const

export const useEditingSessionsStore = defineStore('editing-sessions-store', () => {
  // -----------
  // -- Setup --
  // -----------
  const store = useDefaultStore()
  const propertyStore = usePropertyStore()
  const bulkStore = useProductsBulkStore() // TODO cyclic dependency. could move reactives to a separate store (e.g. bulkStore and remodel this to a composable)
  const route = useRoute()
  const { appTranslate } = useTranslation()

  const saveDebouncer = new Debouncer()

  // ---------------
  // -- Reactives --
  // ---------------
  const previousSessions = ref<StoredEditingSession[]>([])
  const discardedSessions = ref<StoredEditingSession[]>([])
  const previousSessionsHandlerUpdateKey = ref(0)
  const discardedSessionsHandlerUpdateKey = ref(0)

  const current = ref<{ id: string | number; name?: string }>()

  // --------------
  // -- Watchers --
  // --------------

  watch(
    () => previousSessionsHandlerUpdateKey.value,
    () => {
      fetchSessions()
    },
  )

  watch(
    () => discardedSessionsHandlerUpdateKey.value,
    () => {
      fetchSessions(true)
    },
  )

  // ---------------------
  // -- Lifecycle hooks --
  // ---------------------
  onMounted(() => {
    fetchSessions()
    fetchSessions(true)
  })

  // --------------------
  // -- Computed props --
  // --------------------
  const shouldRestoreRoute = computed(() => !!route.query[BlankQueryKey])

  const previousSessionsIds = computed(() => new Set(previousSessions.value.map((session) => session.id)))

  // ---------------
  // -- Functions --
  // ---------------
  const fetchSessions = async (discarded?: boolean) => {
    const result = await DataService.getUserEditingSessions(discarded)
    if (discarded) {
      discardedSessions.value = result
    } else {
      previousSessions.value = result
    }
  }

  const getExistingSessions = (query: LocationQuery, id?: string) =>
    previousSessions.value?.filter((session) => objectsAreEqual(session.query, query) && (!id || id === session.id))

  const getExistingSession = (query: LocationQuery, id?: string) =>
    previousSessions.value?.find((session) => objectsAreEqual(session.query, query) && (!id || id === session.id))

  /**
   *
   * @param debounceTimeMs the debounced delay before the document is prepared and saved on the server
   * @param closeSession set checkedOut false if set to true
   * @param customQuery use if current route.query should not be used
   * @returns
   */
  const saveCurrentSession = async (debounceTimeMs = 2000, closeSession?: boolean) => {
    if (current.value && !bulkStore.isPristine) {
      const filterQuery = Object.entries(route.query).reduce((acc: { [key: string]: LocationQueryValue | LocationQueryValue[] }, [key, value]) => {
        if (key.startsWith('_') || ['exactSearch'].includes(key)) {
          acc[key] = value
        }
        return acc
      }, {})

      const session: StoredEditingSession = {
        id: current.value.id,
        ...(current.value.name && { name: current.value?.name }),
        stagedOptions: propertyStore.stagedPropertyOptionsByComparable,
        dataCulture: store.dataCulture,
        query: filterQuery,
        edited: bulkStore.productInputBySku,
        includedCountries: Array.from(store.includedCountries),
        includeProductsMissingAvailableCountries: store.includeProductsMissingAvailableCountries,
        date: new Date(),
        ...(closeSession && { checkedOut: !!closeSession }),
      }

      return new Promise((resolve) => {
        saveDebouncer.debounce(async () => {
          try {
            await DataService.saveUserEditingSession(session, session.id)
            if (closeSession) {
              previousSessionsHandlerUpdateKey.value++
            }
          } catch (err) {
            store.addMessage(
              new Message(
                appTranslate('Error backing up session'),
                appTranslate("Your recent changes still remain in your browser tab as long as you don't reload the window"),
                'warning',
                10,
              ),
            )
          }
          resolve(0)
        }, debounceTimeMs)
      })
    }
  }

  const removeSession = async (sessionId: number | string, isDiscardedSession?: boolean, overrideDeletePermanently?: boolean, silent?: boolean) => {
    try {
      await DataService.removeEditingSession(sessionId, isDiscardedSession, overrideDeletePermanently, silent)
      if (!isDiscardedSession) {
        previousSessionsHandlerUpdateKey.value++
      }
      discardedSessionsHandlerUpdateKey.value++
    } catch (err) {
      console.log(err)
      store.addMessage(new Message(appTranslate('An error occurred'), undefined, 'danger'))
    }
  }

  const restoreDiscardedSession = async (sessionId: string | number) => {
    await DataService.restoreDiscardedUserEditingSession(sessionId)
    previousSessionsHandlerUpdateKey.value++
    discardedSessionsHandlerUpdateKey.value++
  }

  /**
   * Saves current session if it contains changes, otherwise deletes it if it has been saved previously
   */
  const handleCurrentSessionOnBreak = async () => {
    if (bulkStore.editMode && current.value?.id) {
      if (bulkStore.isPristine) {
        if (!isNew(current.value.id)) {
          await removeSession(current.value.id, undefined, true)
        }
      } else {
        await saveCurrentSession(0, true)
      }
    }
  }

  const isNew = (sessionId: string | number) => !previousSessionsIds.value.has(sessionId)

  return {
    previousSessions,
    discardedSessions,
    current,
    shouldRestoreRoute,
    getExistingSessions,
    getExistingSession,
    saveCurrentSession,
    removeSession,
    getEditedPreviewsEntries: getProductInputEntries,
    restoreDiscardedSession,
    fetchSessions,
    handleCurrentSessionOnBreak,
  }
})
